import React, { FunctionComponent } from "react";
import { connect, MapDispatchToProps, MapStateToProps } from "react-redux";

import { LocalizationProvider, MessageWindow, MessageWindowProps } from "@multiweb/reactComponents";
import { invariant } from "@multiweb/reactUtils";
import { IAppState } from "@multiweb/types/react";
import { createDialogCanceledAction, createDialogConfirmedAction, createHideMessageWindowAction } from "../actions";
import { LOC_OK } from "../locKeys";
import { getWfMessageWindowActionId, getWfMessageWindowContentLocKey, getWfMessageWindowPrimaryButtonLocKey, getWfMessageWindowSecondaryButtonLocKey, isWfMessageWindowVisible } from "../selectors";
import { WorkflowEditorProps } from "../types";

type WfMessageWindowComponentProps = {
    readonly isVisible: boolean;
    readonly actionId: string | null;
    readonly contentLocKey: string | null;
    readonly onConfirmClick: (actionId: string) => void;
    readonly onCancelClick: (actionId: string) => void;
    readonly hideMessageWindow: () => void;
} & MessageWindowProps;

const WfMessageWindowHandlerComponent: FunctionComponent<WfMessageWindowComponentProps> = ({
    isVisible,
    actionId,
    contentLocKey,
    primaryButtonLocKey,
    secondaryButtonLocKey,
    onConfirmClick,
    onCancelClick,
    hideMessageWindow,
}) => {
    invariant(!isVisible || (actionId != null && contentLocKey != null), "Cannot create instance of component WfMessageWindowHandler without actionId and contentLocKey props!");

    return (isVisible && actionId !== null && contentLocKey !== null ?
        <MessageWindow
            onCloseMessageWindow={hideMessageWindow}
            onPrimaryButtonClick={() => onConfirmClick(actionId)}
            onSecondaryButtonClick={() => onCancelClick(actionId)}
            {...{ primaryButtonLocKey, secondaryButtonLocKey }}>
            <LocalizationProvider locKey={contentLocKey} />
        </MessageWindow> : null
    );
};

type StateProps = Pick<WfMessageWindowComponentProps, "actionId" | "isVisible" | "contentLocKey" | "primaryButtonLocKey" | "secondaryButtonLocKey">;

const mapStateToProps: MapStateToProps<StateProps, WorkflowEditorProps, IAppState> = (state, { hostKey }) => ({
    actionId: getWfMessageWindowActionId(state, hostKey),
    contentLocKey: getWfMessageWindowContentLocKey(state, hostKey),
    isVisible: isWfMessageWindowVisible(state, hostKey),
    primaryButtonLocKey: getWfMessageWindowPrimaryButtonLocKey(state, hostKey) ?? LOC_OK,
    secondaryButtonLocKey: getWfMessageWindowSecondaryButtonLocKey(state, hostKey) ?? undefined,
});

type DispatchProps = Pick<WfMessageWindowComponentProps, "onConfirmClick" | "onCancelClick" | "hideMessageWindow">;

const mapDispatchToProps: MapDispatchToProps<DispatchProps, WorkflowEditorProps> = (dispatch, { hostKey }) => ({
    onConfirmClick: (actionId) => dispatch(createDialogConfirmedAction(hostKey, actionId)),
    onCancelClick: (actionId) => dispatch(createDialogCanceledAction(hostKey, actionId)),
    hideMessageWindow: () => dispatch(createHideMessageWindowAction(hostKey)),
});

export const WfMessageWindowHandler = connect(mapStateToProps, mapDispatchToProps)(WfMessageWindowHandlerComponent);
